import { useMemo, useState } from 'react';
import { Field } from 'react-final-form';

import { ErknmActViolationItems } from 'KnmErknmPackage/ErknmViolationsModule/components/ErknmActViolationItems';
import { ErknmDecisionViolationItems } from 'KnmPackage/KnmViolatedRequirementsNpa/components/ErknmDecisionViolationItems';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import {
  IKnmErknmFormViolatedRequirementsMandatory,
  IKnmErknmFormViolatedRequirementsNpa,
} from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { ErknmAgreementsViolationItems } from 'DecisionPackage/DecisionViolationModule/AgreementsComponents/ErknmAgreementsViolationItems';

import styles from './KnmViolatedRequirementsNpaItem.module.scss';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { Textarea } from '../../../UIPackage/MainModule/components/Textarea';

interface IKnmViolatedRequirementsMandatoryAccordionProps {
  name: string;
  isViolationFieldsWatchOnly: boolean;
  watchOnly: boolean;
  isDisabledByDirty?: boolean;
  touched?: boolean;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isMandatoryDisabled: boolean;
  isInsideDecisionsSection?: boolean;
  isInsideAgreementsSection?: boolean;
  inAct?: boolean;
  violatedMandatoryName: string;
  index: number;
  mandatoryValue: IKnmErknmFormViolatedRequirementsMandatory;
  handleRemoveMandatory: (index: number) => void;
  correspondingActMandatoryViolations: IKnmErknmFormViolation[];
  isPaperless: boolean;
  onChangeFixPlanDate?: (date: string | Date) => void;
  mId: string;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  signed?: boolean;
  isDisabledByRepeatCheck?: boolean;
  requirementNpa?: IKnmErknmFormViolatedRequirementsNpa;
  isExpandAll?: boolean;
  extractVersion?: number;
}

export const KnmViolatedRequirementsMandatoryAccordion = ({
  name,
  isViolationFieldsWatchOnly,
  watchOnly,
  touched,
  formRef,
  isMandatoryDisabled,
  isInsideDecisionsSection,
  isInsideAgreementsSection,
  inAct,
  violatedMandatoryName,
  mandatoryValue,
  index,
  handleRemoveMandatory,
  correspondingActMandatoryViolations,
  isPaperless,
  onChangeFixPlanDate,
  mId,
  isKnm,
  isPm,
  isDecision,
  isDisabledByDirty = false,
  signed,
  extractVersion,
  isDisabledByRepeatCheck,
  requirementNpa,
  isExpandAll,
}: IKnmViolatedRequirementsMandatoryAccordionProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const mandatoryFieldName = `${name}.violatedMandatory`;

  const hasError = checkHasErrorInCollapseBlock(formRef, violatedMandatoryName);

  const ViolationsComponents = useMemo(() => {
    if (isInsideAgreementsSection) {
      return (
        <ErknmAgreementsViolationItems
          formRef={formRef}
          watchOnly={isViolationFieldsWatchOnly}
          name={violatedMandatoryName}
          actViolations={correspondingActMandatoryViolations}
          mId={mId}
        />
      );
    }
    if (isInsideDecisionsSection) {
      return (
        <ErknmDecisionViolationItems
          formRef={formRef as IKnmFormRef | undefined}
          watchOnly={isViolationFieldsWatchOnly}
          name={violatedMandatoryName}
          actViolations={correspondingActMandatoryViolations}
          isPaperless={isPaperless}
          onChangeFixPlanDate={onChangeFixPlanDate}
          mId={mId}
          isKnm={isKnm}
          isPm={isPm}
          isDecision={isDecision}
          signed={!!signed}
          isRepeatCheck={isDisabledByRepeatCheck}
          isExpandAll={isExpandAll}
          extractVersion={extractVersion}
        />
      );
    }

    return (
      <ErknmActViolationItems
        formRef={formRef}
        isAddEnabled={!watchOnly}
        watchOnly={isViolationFieldsWatchOnly}
        isDisabledByDirty={isDisabledByDirty}
        name={violatedMandatoryName}
        inAct={!!inAct}
        isPaperless={isPaperless}
        mId={mId}
        isKnm={isKnm}
        isPm={isPm}
        isDecision={isDecision}
        isDisabledByRepeatCheck={isDisabledByRepeatCheck}
        requirementNpa={requirementNpa}
      />
    );
  }, [
    isInsideAgreementsSection,
    isInsideDecisionsSection,
    formRef,
    signed,
    isViolationFieldsWatchOnly,
    violatedMandatoryName,
    correspondingActMandatoryViolations,
    isPaperless,
    onChangeFixPlanDate,
    mId,
    isKnm,
    isPm,
    isDecision,
    watchOnly,
    isDisabledByDirty,
    inAct,
    requirementNpa,
  ]);

  return (
    <Field name={`${violatedMandatoryName}.mandatory`}>
      {({ meta }) => (
        <KnmCollapse
          id={isDecision ? `${mandatoryFieldName}[${index}]` : mId}
          theme="fields"
          title={`Обязательное требование №${index + 1}`}
          onClose={
            !watchOnly && !isMandatoryDisabled && !isDisabledByDirty && !isDisabledByRepeatCheck
              ? () => handleRemoveMandatory(index)
              : undefined
          }
          openByDefault={true}
          changeOpenedCallback={setIsKnmCollapseOpened}
          error={isKnmCollapseOpened && hasError}
        >
          <>
            <div className={styles.InnerAccordionWrapper}>
              <FieldBlock
                label={'Содержание требования'}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <Textarea disabled={true} value={mandatoryValue.mandatory.title} />
              </FieldBlock>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: 'Значимость последствий нарушения',
                  field: `${mandatoryFieldName}[${index}].category`,
                  isPm,
                  isKnm,
                  isDecision,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
                id={isDecision ? `${mandatoryFieldName}[${index}].category` : `${mId}.category`}
              >
                <SelectInputFieldAutoOptions
                  name={`${mandatoryFieldName}[${index}].category`}
                  optionName={CommonCatalogsNamesEnum.MANDATORY_CATEGORIES}
                  disabled={watchOnly || isMandatoryDisabled || isDisabledByDirty}
                />
              </FieldBlock>
            </div>
            {ViolationsComponents}
            {touched && meta.error && <p className="shared-input-error shared-row__small-margin">{meta.error}</p>}
          </>
        </KnmCollapse>
      )}
    </Field>
  );
};
